import { useState } from 'react';

import { Download, Info, Plus, Users } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ehPessoaJuridica } from '@/nucleo/pessoaNatureza';
import { formatarData } from '@/nucleo/formato/datas';
import { mascaraCpf, mascaraTelefone } from '@/nucleo/formato/documentos';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import type { Credenciado } from './contrato';
import { useRepresentantesDoCredenciado } from './dados';

interface AbaRepresentantesProps {
  credenciadoId: string;
  tipoDoCredenciado: Credenciado['tipo'];
}

/**
 * Aba "Representantes" — os representantes legais de `public.representantes`, pela
 * API. O CPF chega mascarado e a leitura fica registrada em `acessos_dado_pessoal`.
 *
 * Nenhuma jornada escreve essa tabela hoje, então lista vazia é o estado comum: a aba
 * diz isso em vez de parecer quebrada. "Novo Representante" e "Importar do SGTP" não
 * gravam — mostram o aviso de indisponibilidade, e o diálogo traz aviso fixo.
 */
export function AbaRepresentantes({ credenciadoId, tipoDoCredenciado }: AbaRepresentantesProps) {
  const {
    data: representantes,
    isLoading,
    isError,
    error,
    refetch,
  } = useRepresentantesDoCredenciado(credenciadoId);
  const [dialogAberto, setDialogAberto] = useState(false);
  const ehPJ = ehPessoaJuridica(tipoDoCredenciado);

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex flex-wrap justify-end gap-2">
          {ehPJ && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
            >
              <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Importar do SGTP
            </Button>
          )}
          <Button size="sm" onClick={() => setDialogAberto(true)}>
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Novo Representante
          </Button>
        </div>

        {isLoading ? (
          <Spinner size="sm" />
        ) : isError ? (
          <ErrorState
            variant="load-failed"
            description={
              error?.aviso ??
              'Não foi possível buscar os representantes agora. Tente novamente em instantes.'
            }
            primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
          />
        ) : !representantes || representantes.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum representante"
            description="Este credenciado não tem representante legal cadastrado."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Ativo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {representantes.map((representante) => (
                  <TableRow key={representante.id}>
                    <TableCell className="font-medium">{representante.nome ?? '—'}</TableCell>
                    <TableCell className="font-mono">
                      {representante.cpf_mascarado ?? '—'}
                    </TableCell>
                    <TableCell>{representante.cargo ?? '—'}</TableCell>
                    <TableCell>
                      {representante.data_inicio ? formatarData(representante.data_inicio) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={representante.ativo ? 'default' : 'outline'}>
                        {representante.ativo ? 'Sim' : 'Não'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <NovoRepresentanteDialog open={dialogAberto} onOpenChange={setDialogAberto} />
    </Card>
  );
}

/**
 * O formulário do protótipo, sem gravação: "Criar" mostra o aviso de indisponibilidade
 * e o diálogo fica aberto, com o que foi digitado — fechar e limpar faria parecer que
 * o representante foi criado.
 */
function NovoRepresentanteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [cargo, setCargo] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo representante"
      description="Cadastra um representante legal para este credenciado."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL)}
            disabled={!nome || !cpf || !cargo}
          >
            Criar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            O cadastro de representante ainda não está disponível. Você pode preencher e conferir o
            formulário, mas nada é registrado.
          </AlertDescription>
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="representante-nome">Nome</Label>
            <Input
              id="representante-nome"
              className="mt-1.5"
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="representante-cpf">CPF</Label>
            <Input
              id="representante-cpf"
              className="mt-1.5"
              inputMode="numeric"
              value={cpf}
              onChange={(evento) => setCpf(mascaraCpf(evento.target.value))}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="representante-cargo">Cargo</Label>
          <Input
            id="representante-cargo"
            className="mt-1.5"
            value={cargo}
            onChange={(evento) => setCargo(evento.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="representante-email">E-mail</Label>
            <Input
              id="representante-email"
              className="mt-1.5"
              type="email"
              value={email}
              onChange={(evento) => setEmail(evento.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="representante-telefone">Telefone</Label>
            <Input
              id="representante-telefone"
              className="mt-1.5"
              inputMode="tel"
              value={telefone}
              onChange={(evento) => setTelefone(mascaraTelefone(evento.target.value))}
            />
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}
